import { Component, OnInit, DoCheck, Input, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Person } from '../../../models/person';
import { Usef_person } from '../../../models/usef/usef_person';
import { PersonService } from '../../../providers/person.service';
import { SmsAbstractAppPage } from '../../sms-abstract-app-page';

function normalize(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

function levenshtein(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => i === 0 ? j : j === 0 ? i : 0)
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
}

function isFuzzyNameMatch(a: string, b: string): boolean {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb || na.length < 3 || nb.length < 3) return false;
  if (na === nb) return true;
  if (na.length >= 4 && nb.length >= 4 && (na.startsWith(nb) || nb.startsWith(na))) return true;
  if (levenshtein(na, nb) <= 2) return true;
  return false;
}

function fullName(p: Person): string {
  return [(p.first_name || ''), (p.last_name || '')].join(' ').trim();
}

@Component({
  standalone: false,
  selector: 'app-modal-add-person',
  templateUrl: './modal-add-person.component.html',
  styleUrls: ['./modal-add-person.component.css']
})
export class ModalAddPersonComponent extends SmsAbstractAppPage implements OnInit, DoCheck {
  @Input() person: Person;
  @Input() usef_person: Usef_person;
  @Output() person_saved$: EventEmitter<Person> = new EventEmitter<Person>();
  @Output() existing_person_selected$: EventEmitter<Person> = new EventEmitter<Person>();

  person_list: Person[] = [];
  name_matches: Person[] = [];
  id_conflicts: Person[] = [];

  private _lastCheckedKey = '';

  constructor(
    public activeModal: NgbActiveModal,
    private personService: PersonService
  ) {
    super();
  }

  ngOnInit() {
    this.personService.getList().then(people => {
      this.person_list = people;
      this.runDuplicateCheck();
    }).catch(() => {
      // non-critical — duplicate check silently degrades if list unavailable
    });
  }

  ngDoCheck() {
    if (!this.person) return;
    const key = [
      this.person.first_name,
      this.person.last_name,
      this.person.usef_number,
      this.person.ec_number,
      this.person.fei_id,
      this.person.ushja_id,
    ].join('|');
    if (key !== this._lastCheckedKey) {
      this._lastCheckedKey = key;
      this.runDuplicateCheck();
    }
  }

  private runDuplicateCheck() {
    if (!this.person_list.length) return;

    // Registry ID conflicts — definitive matches
    this.id_conflicts = this.person_list.filter(p => {
      return (
        (this.person.usef_number      && p.usef_number      && this.person.usef_number      === p.usef_number)      ||
        (this.person.usef_person_uuid && p.usef_person_uuid && this.person.usef_person_uuid === p.usef_person_uuid) ||
        (this.person.ec_number        && p.ec_number        && this.person.ec_number        === p.ec_number)        ||
        (this.person.fei_id           && p.fei_id           && this.person.fei_id           === p.fei_id)           ||
        (this.person.ushja_id         && p.ushja_id         && this.person.ushja_id         === p.ushja_id)
      );
    });

    // Fuzzy name matches — soft warning (exclude anything already flagged as an ID conflict)
    const conflictIds = new Set(this.id_conflicts.map(p => p.id));
    const newName = fullName(this.person);
    if (newName.length >= 3) {
      this.name_matches = this.person_list.filter(p =>
        !conflictIds.has(p.id) && isFuzzyNameMatch(newName, fullName(p))
      );
    } else {
      this.name_matches = [];
    }
  }

  idConflictReason(existing: Person): string {
    const reasons: string[] = [];
    if (this.person.usef_number && existing.usef_number === this.person.usef_number)
      reasons.push(`USEF# ${existing.usef_number}`);
    if (this.person.usef_person_uuid && existing.usef_person_uuid === this.person.usef_person_uuid)
      reasons.push(`USEF UUID`);
    if (this.person.ec_number && existing.ec_number === this.person.ec_number)
      reasons.push(`EC# ${existing.ec_number}`);
    if (this.person.fei_id && existing.fei_id === this.person.fei_id)
      reasons.push(`FEI ${existing.fei_id}`);
    if (this.person.ushja_id && existing.ushja_id === this.person.ushja_id)
      reasons.push(`USHJA# ${existing.ushja_id}`);
    return reasons.join(', ');
  }

  personDisplayName(p: Person): string {
    return fullName(p) || 'this person';
  }

  handleUseExisting(person: Person) {
    this.existing_person_selected$.emit(person);
  }

  handleSavePerson(person: Person) {
    this.person_saved$.emit(person);
  }
}
